import { DateTime } from 'luxon';
import { nanoid } from 'nanoid';
import type { Channel } from 'pusher-js';
import { PusherEvent } from '@getaccept/pusher';
import type {
  ChatParticipant,
  Conversation,
  Message,
  MessageMetadata,
} from '@getaccept/dsr-shared';
import { ChatHelper, MessageType } from '@getaccept/dsr-shared';
import { useChat } from '@getaccept/dsr-shared/src/chat/composables/chat.composable';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import { defineStore } from 'pinia';
import { ChatService } from '../../../api/chat/chat.service';
import { pusherClient } from '../../../api/pusher/pusher-client';

export const useChatPublishedStore = defineStore('chatPublished', () => {
  const {
    chat,
    addMessage,
    mapChatParticipant,
    setDefaultConversation,
    setReplyMessage,
    useConversationLastReadList,
    setCurrentConversation,
    handleNewConversationParticipants,
    handleSetCurrentConversation,
    setCreatingNewConversation,
    lastGeneralChatMessage,
    newConversationParticipants,
    creatingNewConversation,
  } = useChat();

  const loadConversationList = async () => {
    chat.error = false;
    chat.loadingConversationList = false;
    try {
      chat.conversations = await ChatService.loadConversationList();
    } catch {
      chat.error = true;
    } finally {
      chat.loadingConversationList = false;
    }
  };

  const loadConversationDetails = async () => {
    chat.error = false;
    chat.loadingConversationDetails = true;
    try {
      chat.conversations = await Promise.all(
        chat.conversations.map(
          async conversation => await ChatService.loadConversationDetails(conversation.id)
        )
      );
    } catch {
      chat.error = true;
    } finally {
      chat.loadingConversationDetails = false;
    }
  };

  const createConversation = async (recipientId: string, text: string) => {
    try {
      let newConversation = await ChatService.createConversation(newConversationParticipants.value);
      newConversation = {
        ...newConversation,
        messages: [],
      };
      chat.conversations.push(newConversation);
      setCurrentConversation(newConversation.id);
      ChatHelper.scrollConversationIntoView();
    } catch {
      chat.error = true;
    } finally {
      createMessage(text, recipientId);
    }
  };

  const createMessage = async (text: string, recipientId: string) => {
    if (!chat.conversation) {
      return;
    }

    const metadata: MessageMetadata = chat.replyMessage
      ? { repliedTo: chat.replyMessage.id, mentions: [] }
      : undefined;

    const tempMessage = {
      id: nanoid(),
      text,
      owner: {
        recipientId,
      },
      metadata,
      createdAt: DateTime.now().toMillis(),
      isDraft: true,
      type: MessageType.Public,
      reads: [],
    };

    const updatedConversation = {
      ...chat.conversation,
      messages: [...chat.conversation.messages, tempMessage],
    };

    chat.conversations = chat.conversations.map(conversation =>
      conversation.id === updatedConversation.id ? updatedConversation : conversation
    );

    try {
      const message = await ChatService.addMessage(
        updatedConversation.id,
        text,
        chat.replyMessage?.id
      );
      addMessage(message, tempMessage.id, updatedConversation, true);
    } catch (e) {
      const errorMessage = { ...tempMessage, error: true };
      addMessage(errorMessage, tempMessage.id, updatedConversation, true);
    }
  };

  const setMessageReadBy = async (messageId: string) => {
    try {
      await ChatService.setMessageReadBy(chat.conversation.id, messageId);
    } catch (e) {
      console.error(e);
    }
  };

  const setTypingStatus = async (isTyping: boolean) => {
    try {
      await ChatService.setTypingStatus(chat.conversation.id, isTyping);
    } catch (e) {
      console.error(e);
    }
  };

  const deleteMessage = async (messageId: string) => {
    const message = chat.conversation.messages.find(message => message.id === messageId);

    addMessage(
      {
        ...message,
        text: `${t('deleting_message')}...`,
        isDraft: true,
      },
      messageId,
      chat.conversation,
      true
    );

    try {
      await ChatService.deleteMessage(chat.conversation.id, messageId);

      addMessage(
        {
          ...message,
          text: '',
          metadata: undefined,
          deletedAt: DateTime.now().toMillis(),
          isDraft: false,
        },
        messageId,
        chat.conversation,
        true
      );
    } catch {
      addMessage(message, messageId, chat.conversation, true);
    }
  };

  const addMessageCreatedListener = (channel: Channel) => {
    pusherClient.listen(
      channel,
      PusherEvent.MessageCreated,
      (event: { conversation: Partial<Conversation>; message: Message }) => {
        if (!event.message) {
          return;
        }

        const updatedConversation = chat.conversations.find(
          conversation => conversation.id === event.conversation.id
        );

        if (!updatedConversation) {
          return;
        }

        addMessage(event.message, event.message.id, updatedConversation, false);
      }
    );
  };

  const addTypingStatusListener = (channel: Channel, recipients: Recipient[], user: User) => {
    pusherClient.listen(
      channel,
      PusherEvent.Typing,
      (event: { conversation: Partial<Conversation>; participant: Partial<ChatParticipant> }) => {
        chat.typing = [
          ...chat.typing,
          {
            conversationId: event.conversation.id,
            participant: mapChatParticipant(event.participant, recipients, user),
          },
        ];
      }
    );

    pusherClient.listen(
      channel,
      PusherEvent.StoppedTyping,
      (event: { conversation: Partial<Conversation>; participant: Partial<ChatParticipant> }) => {
        chat.typing = chat.typing.filter(({ conversationId, participant }) => {
          const isSameConversation = conversationId === event.conversation.id;
          const isSameParticipant = ChatHelper.isSameParticipant(event.participant, participant);
          return !(isSameConversation && isSameParticipant);
        });
      }
    );
  };

  const addMessageReadListener = (channel: Channel) => {
    pusherClient.listen(channel, PusherEvent.MessageRead, event => {
      const updatedConversation = chat.conversations?.find(
        conversation => conversation.id === event.conversation.id
      );
      if (!updatedConversation) {
        return;
      }

      const message = updatedConversation.messages.find(({ id }) => id === event.message.id);
      const read = {
        timestamp: DateTime.now().toMillis(),
        participant: { recipientId: event.recipientId, userId: event.userId },
      };

      addMessage(
        { ...message, reads: [...message.reads, read] },
        message.id,
        updatedConversation,
        true
      );
    });
  };

  const addMessageDeletedListener = (channel: Channel) => {
    pusherClient.listen(channel, PusherEvent.MessageDeleted, event => {
      const updatedConversation = chat.conversations?.find(
        conversation => conversation.id === event.conversation.id
      );
      if (!updatedConversation) {
        return;
      }

      const message = updatedConversation.messages.find(({ id }) => id === event.message.id);

      addMessage(
        { ...message, deletedAt: DateTime.now().toMillis(), text: '', metadata: undefined },
        message.id,
        updatedConversation,
        true
      );
    });
  };

  return {
    chat,
    setDefaultConversation,
    loadConversationList,
    loadConversationDetails,
    createMessage,
    setMessageReadBy,
    setTypingStatus,
    deleteMessage,
    addMessageCreatedListener,
    addTypingStatusListener,
    addMessageReadListener,
    addMessageDeletedListener,
    setReplyMessage,
    useConversationLastReadList,
    setCurrentConversation,
    createConversation,
    handleNewConversationParticipants,
    handleSetCurrentConversation,
    setCreatingNewConversation,
    lastGeneralChatMessage,
    creatingNewConversation,
    newConversationParticipants,
  };
});
